import type { ChatMessage as LiveChatMessage } from '../context/ChatContext';
import type { ChatMessage as HistoryChatMessage } from '../types';
import type { SavedPromptFromChatRequest } from '../types/preferences';
import { formatLocalDate } from './adminFormatters';

interface PromptPairingMessage {
  id: string;
  content: string;
  timestamp?: string | Date;
  metadata?: Record<string, unknown>;
}

type PromptPairingKind = 'assistant' | 'synthetic' | 'user';

const getSourcePromptLocalDate = (timestamp?: string | Date): string | undefined => {
  if (!timestamp) {
    return undefined;
  }

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.valueOf())) {
    return undefined;
  }

  return formatLocalDate(date);
};

const pairAssistantResponsesWithSourcePrompts = <Message extends PromptPairingMessage>(
  messages: Message[],
  getKind: (message: Message) => PromptPairingKind,
): ReadonlyMap<string, SavedPromptFromChatRequest> => {
  const sourcePrompts = new Map<string, SavedPromptFromChatRequest>();
  let latestUserPrompt: SavedPromptFromChatRequest | null = null;

  for (const message of messages) {
    const isSynthetic = Boolean(message.metadata?.syntheticMessageKey);
    const kind = getKind(message);

    if (kind === 'user' && !isSynthetic) {
      const sourcePrompt = message.content.trim();
      const sourcePromptLocalDate = getSourcePromptLocalDate(message.timestamp);
      latestUserPrompt = sourcePrompt
        ? {
            source_prompt: sourcePrompt,
            ...(sourcePromptLocalDate ? { source_prompt_local_date: sourcePromptLocalDate } : {}),
          }
        : null;
      continue;
    }

    if (kind === 'assistant' && !isSynthetic && latestUserPrompt) {
      sourcePrompts.set(message.id, latestUserPrompt);
    }
  }

  return sourcePrompts;
};

export const pairLiveAssistantResponsesWithSourcePrompts = (
  messages: LiveChatMessage[],
): ReadonlyMap<string, SavedPromptFromChatRequest> => pairAssistantResponsesWithSourcePrompts(
  messages,
  (message) => message.direction === 'outgoing'
      ? 'user'
      : message.direction === 'incoming'
        ? 'assistant'
        : 'synthetic',
);

export const pairHistoryAssistantResponsesWithSourcePrompts = (
  messages: HistoryChatMessage[],
): ReadonlyMap<string, SavedPromptFromChatRequest> => pairAssistantResponsesWithSourcePrompts(
  messages,
  (message) => message.role === 'user'
      ? 'user'
      : message.role === 'assistant'
        ? 'assistant'
        : 'synthetic',
);